// components
import withFieldArrayDrawer from 'components/shared/CRUD/components/InnerPage/components/withFieldArrayDrawer';
import {
  BenefitStartDateField,
  BUHBlockedField,
  CodeExtField,
  CodeExtSecondaryField,
  CodeField,
  CodeImpField,
  CodeImpSecondaryField,
  EmployerField,
  FinancialCostCenterField,
  InvoiceCreditBlockedField,
  InvoiceDebitBlockedField,
  ChildcareTypeField,
  NameField,
  NameShortField,
  ParentCostCenterField,
  PlanningExportField,
  ValidFromField,
  ValidToField,
  VATDeductibleField
} from '../../fields/SubEntityFields';
import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import { FieldCaption } from './EditCostCenterDetail.styles';

// hooks
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';
import { useFieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useFindIndexFromFieldsArray from 'components/shared/CRUD/hooks/useFindIndexFromFieldsArray';
import { useTranslation } from 'react-i18next';
import { useHasChildcareAccess } from 'components/admin-portal/Tables/Settings/PlanUnits/hooks/useHasChildcareAccess';

// types
import { CostCenterDetailFlag } from 'components/admin-portal/Tables/Settings/PlanUnits/types';
import { ExtendedFieldArrayWithId } from './EditCostCenterDetail.types';
import { OptionWithValidFrom } from 'components/admin-portal/Tables/Settings/PlanUnits/hooks/useCreateCostCenterDetailOptions.types';

const getLastAddedFieldId = (fields: FieldArrayWithId[]) =>
  fields.slice(-1)[0]?.id;

const FullWidthBox: React.FC<PropsWithChildren<any>> = ({ children }) => {
  return <GridCompact12>{children}</GridCompact12>;
};

const HalfWidthBox: React.FC<PropsWithChildren<any>> = ({ children }) => {
  return <GridCompact6>{children}</GridCompact6>;
};

const EditCostCenterDetail: React.FC<{ rowId: number }> = ({ rowId }) => {
  const { fields }: { fields: ExtendedFieldArrayWithId[] } =
    useFieldArrayContext();
  const [parentCostCenterOptions, setParentCostCenterOptions] = useState<
    OptionWithValidFrom[]
  >([]);
  const [financialCostCenterOptions, setFinancialCostCenterFieldOptions] =
    useState<OptionWithValidFrom[]>([]);
  const fieldIndex = useFindIndexFromFieldsArray(
    rowId ?? getLastAddedFieldId(fields),
    fields
  );
  const selectedBrDynId = fields && fields[fieldIndex]?.brDynId;
  const parentBrDynIdValue = fields && fields[fieldIndex]?.parentBrDynId?.value;
  const finBrDynIdValue = fields && fields[fieldIndex]?.finBrDynId?.value;

  const { resetField } = useFormContext();
  const { t } = useTranslation('common');
  const hasChildcareAccess = useHasChildcareAccess();

  useEffect(() => {
    if (
      (parentBrDynIdValue && parentCostCenterOptions.length) ||
      (finBrDynIdValue && financialCostCenterOptions.length)
    ) {
      const parentCostCenterOption = parentCostCenterOptions.find(
        (option) => option.value === parentBrDynIdValue
      );
      const financialCostCenterOption = financialCostCenterOptions.find(
        (option) => option.value === finBrDynIdValue
      );

      parentCostCenterOption &&
        resetField(`costCenterDetails[${fieldIndex}].parentBrDynId`, {
          defaultValue: parentCostCenterOption
        });
      financialCostCenterOption &&
        resetField(`costCenterDetails[${fieldIndex}].finBrDynId`, {
          defaultValue: financialCostCenterOption
        });
    }
  }, [
    parentBrDynIdValue,
    finBrDynIdValue,
    fieldIndex,
    resetField,
    parentCostCenterOptions
  ]);

  return (
    <GridContainer>
      <FullWidthBox>
        <CodeField name={`costCenterDetails[${fieldIndex}].code`} />
      </FullWidthBox>

      <FullWidthBox>
        <NameField name={`costCenterDetails[${fieldIndex}].name`} />
      </FullWidthBox>

      <FullWidthBox>
        <NameShortField name={`costCenterDetails[${fieldIndex}].nameShort`} />
      </FullWidthBox>

      <HalfWidthBox>
        <ValidFromField name={`costCenterDetails[${fieldIndex}].valid.from`} />
      </HalfWidthBox>

      <HalfWidthBox>
        <ValidToField name={`costCenterDetails[${fieldIndex}].valid.to`} />
      </HalfWidthBox>

      <FullWidthBox>
        <EmployerField name={`costCenterDetails[${fieldIndex}].employerId`} />
      </FullWidthBox>

      {hasChildcareAccess && (
        <FullWidthBox>
          <ChildcareTypeField
            name={`costCenterDetails[${fieldIndex}].childcareType`}
          />
          <FieldCaption variant="caption">
            {t('costCenterDetail.childcareTypeHelp')}
          </FieldCaption>
        </FullWidthBox>
      )}

      <HalfWidthBox>
        <CodeExtField name={`costCenterDetails[${fieldIndex}].codeExt`} />
      </HalfWidthBox>

      <HalfWidthBox>
        <CodeExtSecondaryField
          name={`costCenterDetails[${fieldIndex}].codeExtSecondary`}
        />
      </HalfWidthBox>

      <HalfWidthBox>
        <CodeImpField name={`costCenterDetails[${fieldIndex}].codeImp`} />
      </HalfWidthBox>

      <HalfWidthBox>
        <CodeImpSecondaryField
          name={`costCenterDetails[${fieldIndex}].codeImpSecondary`}
        />
      </HalfWidthBox>

      <FullWidthBox>
        <BenefitStartDateField
          name={`costCenterDetails[${fieldIndex}].benefitStartDate`}
        />
      </FullWidthBox>

      <FullWidthBox>
        <FinancialCostCenterField
          name={`costCenterDetails[${fieldIndex}].finBrDynId`}
          setFinancialCostCenterFieldOptions={
            setFinancialCostCenterFieldOptions
          }
        />
      </FullWidthBox>

      <FullWidthBox>
        <ParentCostCenterField
          name={`costCenterDetails[${fieldIndex}].parentBrDynId`}
          selectedBrDynId={selectedBrDynId}
          setParentCostCenterOptions={setParentCostCenterOptions}
        />
      </FullWidthBox>

      <FullWidthBox>
        <BUHBlockedField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.BUH_BLOCKED}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <PlanningExportField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.PLANNING_EXPORT}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <VATDeductibleField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.VAT_DEDUCTIBLE}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <InvoiceDebitBlockedField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.INVOICE_DEBIT_BLOCKED}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <InvoiceCreditBlockedField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.INVOICE_CREDIT_BLOCKED}]`}
        />
      </FullWidthBox>
    </GridContainer>
  );
};

export default withFieldArrayDrawer(EditCostCenterDetail, 'costCenterDetails');
